import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormGroup,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  TextField,
  Chip,
  Box,
  Stack
} from '@mui/material';
import { buildEventsApiUrl } from '../config/api';
import { buildAuthHeaders, buildJsonAuthHeaders } from '../utils/authFetch';

interface Item {
  id: string;
  name: string;
  category?: string;
  tags?: string[];
  isCashItem?: boolean;
  cashValueUSD?: number;
}

interface WantListSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  eventId: string;
  targetItem: Item | null;
  myItems: Item[];
  userId: string;
  tradeLocked?: boolean;
  onCashItemCreated?: (item: Item) => void;
  existingInterest?: {
    id?: string;
    candidateOfferedItemIDs?: string[];
    cashOfferUSD?: number;
    status?: string;
  } | null;
}

const isCashItem = (item?: Item | null) => Boolean(item?.isCashItem || String(item?.category || '').toLowerCase() === 'cash offer');

const WantListSelectionModalBody: React.FC<WantListSelectionModalProps> = ({
  onClose,
  onSaved,
  eventId,
  targetItem,
  myItems,
  userId,
  tradeLocked = false,
  onCashItemCreated,
  existingInterest
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(() => existingInterest?.candidateOfferedItemIDs || []);
  const [saving, setSaving] = useState(false);
  const [creatingCashItem, setCreatingCashItem] = useState(false);
  const [cashCreateAmount, setCashCreateAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedPriceTags, setSelectedPriceTags] = useState<string[]>([]);

  const selectableItems = useMemo(() => {
    const targetIsCash = isCashItem(targetItem);
    return myItems.filter((item) => {
      if (item.id === targetItem?.id) return false;
      if (targetIsCash && isCashItem(item)) return false;
      return true;
    });
  }, [myItems, targetItem]);

  const availableCategories = useMemo(() => {
    return Array.from(new Set(selectableItems.map((item) => String(item.category || '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b));
  }, [selectableItems]);

  const availablePriceTags = useMemo(() => {
    return Array.from(
      new Set(
        selectableItems.flatMap((item) => (Array.isArray(item.tags) ? item.tags : []).filter((tag) => /^price:/i.test(tag) || /budget|mid|premium|value band/i.test(tag)))
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [selectableItems]);

  const filteredItems = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return selectableItems.filter((item) => {
      const nameMatches = !query || item.name.toLowerCase().includes(query);
      const categoryMatches = selectedCategories.length === 0 || selectedCategories.includes(String(item.category || '').trim());
      const itemTags = Array.isArray(item.tags) ? item.tags : [];
      const priceMatches = selectedPriceTags.length === 0 || selectedPriceTags.every((tag) => itemTags.includes(tag));
      return nameMatches && categoryMatches && priceMatches;
    });
  }, [selectableItems, searchText, selectedCategories, selectedPriceTags]);

  const toggleItem = (itemId: string) => {
    setSelectedIds(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) => prev.includes(category) ? prev.filter((value) => value !== category) : [...prev, category]);
  };

  const togglePriceTag = (tag: string) => {
    setSelectedPriceTags((prev) => prev.includes(tag) ? prev.filter((value) => value !== tag) : [...prev, tag]);
  };

  const handleCreateCashItem = async () => {
    const normalizedAmount = Number(cashCreateAmount);
    if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
      setError('Enter a valid cash amount greater than zero.');
      return;
    }

    try {
      setCreatingCashItem(true);
      setError(null);
      const createHeaders = buildJsonAuthHeaders(userId);
      const createItemResponse = await fetch(buildEventsApiUrl('/api/items'), {
        method: 'POST',
        headers: createHeaders,
        body: JSON.stringify({
          userId,
          name: `$${Number.isInteger(normalizedAmount) ? normalizedAmount : normalizedAmount.toFixed(2)} Cash`,
          isCashItem: true,
          cashValueUSD: normalizedAmount,
          category: 'Cash Offer',
          condition: 'Cash',
          images: []
        })
      });
      const createItemData = await createItemResponse.json();
      if (!createItemResponse.ok) {
        throw new Error(createItemData.error || 'Failed to create cash item');
      }

      const createdItem = createItemData.item || null;
      if (!createdItem?.id) {
        throw new Error('Cash item was created but no item id was returned');
      }

      const addToEventResponse = await fetch(buildEventsApiUrl(`/api/events/${eventId}/items`), {
        method: 'POST',
        headers: createHeaders,
        body: JSON.stringify({ itemIds: [createdItem.id], userId })
      });
      const addToEventData = await addToEventResponse.json();
      if (!addToEventResponse.ok) {
        throw new Error(addToEventData.error || 'Failed to add cash item to event');
      }

      const normalizedCreatedItem = {
        id: createdItem.id,
        name: createdItem.name,
        category: createdItem.category,
        isCashItem: true,
        cashValueUSD: Number(createdItem.cashValueUSD || normalizedAmount),
        tags: Array.isArray(createdItem.tags) ? createdItem.tags : []
      };
      onCashItemCreated?.(normalizedCreatedItem);
      setSelectedIds((prev) => [...new Set([...prev, createdItem.id])]);
      setCashCreateAmount('');
    } catch (err: any) {
      setError(err.message || 'Failed to create cash item');
    } finally {
      setCreatingCashItem(false);
    }
  };

  const handleWithdraw = async () => {
    if (tradeLocked) {
      setError('Want lists are locked because the trade cutoff has passed.');
      return;
    }
    if (!targetItem || !existingInterest?.id) return;

    try {
      setSaving(true);
      setError(null);

      const deleteHeaders = buildAuthHeaders(userId);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/items/${targetItem.id}/interests?userId=${userId}`), {
        method: 'DELETE',
        headers: deleteHeaders
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to remove saved want');
      }

      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to remove saved want');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (tradeLocked) {
      setError('Want lists are locked because the trade cutoff has passed.');
      return;
    }
    if (!targetItem) return;

    if (selectedIds.length === 0) {
      setError('Select at least one event item to save this want.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const saveHeaders = buildJsonAuthHeaders(userId);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/items/${targetItem.id}/interests`), {
        method: 'POST',
        headers: saveHeaders,
        body: JSON.stringify({
          requestingUserID: userId,
          candidateOfferedItemIDs: selectedIds,
          cashOfferUSD: 0
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save want list selection');
      }

      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save want list selection');
    } finally {
      setSaving(false);
    }
  };

  return (
      <>
        <DialogTitle>Add to Want List</DialogTitle>
        <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Add <strong>{targetItem?.name || 'this item'}</strong> to the want lists for any of your event items.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select which of your event items would accept this target. Cash offer items work like normal event items, but cash cannot target other cash.
        </Typography>

        {tradeLocked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Want lists are locked because the trade cutoff has passed for this event.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, alignItems: { sm: 'center' } }}>
          <TextField
            label="Quick cash offer ($)"
            type="number"
            size="small"
            value={cashCreateAmount}
            onChange={(e) => setCashCreateAmount(e.target.value)}
            inputProps={{ min: 0.01, step: '0.01' }}
            disabled={tradeLocked || saving || creatingCashItem}
          />
          <Button variant="outlined" onClick={handleCreateCashItem} disabled={tradeLocked || saving || creatingCashItem}>
            {creatingCashItem ? 'Creating Cash Offer...' : 'Create Cash Offer'}
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {selectableItems.length > 0 && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Search your event items"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Filter by item name"
            />

            {availableCategories.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Filter by category
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {availableCategories.map((category) => {
                    const active = selectedCategories.includes(category);
                    return (
                      <Chip
                        key={category}
                        label={category}
                        clickable
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        onClick={() => toggleCategory(category)}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {availablePriceTags.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Filter by price tags
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {availablePriceTags.map((tag) => {
                    const active = selectedPriceTags.includes(tag);
                    return (
                      <Chip
                        key={tag}
                        label={tag}
                        clickable
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        onClick={() => togglePriceTag(tag)}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </Stack>
        )}

        <FormGroup>
          {filteredItems.map(item => (
            <FormControlLabel
              key={item.id}
              control={<Checkbox checked={selectedIds.includes(item.id)} onChange={() => toggleItem(item.id)} disabled={tradeLocked || saving} />}
              label={item.name}
            />
          ))}
        </FormGroup>

        {selectableItems.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You need at least one eligible event item before building want lists. Cash items cannot target other cash items.
          </Alert>
        )}

        {selectableItems.length > 0 && filteredItems.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No matching event items for the current search/category/price filters.
          </Alert>
        )}

        </DialogContent>
        <DialogActions>
        <Button onClick={onClose} disabled={saving || creatingCashItem}>Cancel</Button>
        {existingInterest?.id && existingInterest?.status === 'active' && (
          <Button onClick={handleWithdraw} color="error" disabled={saving || creatingCashItem || tradeLocked}>
            {saving ? 'Removing...' : 'Remove Want'}
          </Button>
        )}
        <Button onClick={handleSave} variant="contained" disabled={tradeLocked || saving || creatingCashItem || !targetItem}>
          {saving ? 'Saving...' : 'Save Want'}
        </Button>
        </DialogActions>
      </>
  );
};

const WantListSelectionModal: React.FC<WantListSelectionModalProps> = ({
  open,
  onClose,
  onSaved,
  eventId,
  targetItem,
  myItems,
  userId,
  tradeLocked = false,
  onCashItemCreated,
  existingInterest
}) => {
  const selectionKey = `${targetItem?.id || 'none'}:${existingInterest?.id || 'none'}:${(existingInterest?.candidateOfferedItemIDs || []).slice().sort().join(',')}:${existingInterest?.cashOfferUSD || 0}`;

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <WantListSelectionModalBody
        key={selectionKey}
        open={open}
        onClose={onClose}
        onSaved={onSaved}
        eventId={eventId}
        targetItem={targetItem}
        myItems={myItems}
        userId={userId}
        tradeLocked={tradeLocked}
        onCashItemCreated={onCashItemCreated}
        existingInterest={existingInterest}
      />
    </Dialog>
  );
};

export default WantListSelectionModal;
